import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Shell } from "./components/layout/Shell";
import { Spinner, ToastProvider } from "./components/ui";
import { AdminScreen } from "./features/admin/AdminScreen";
import { AssistantScreen } from "./features/assistant/AssistantScreen";
import { AuditScreen } from "./features/audit/AuditScreen";
import { DocumentDetailScreen } from "./features/documents/DocumentDetailScreen";
import { DocumentListScreen } from "./features/documents/DocumentListScreen";
import { EditorScreen } from "./features/documents/EditorScreen";
import { HomeScreen } from "./features/home/HomeScreen";
import { IngestionScreen } from "./features/ingestion/IngestionScreen";
import { LoginScreen } from "./features/auth/LoginScreen";
import { LandingScreen } from "./features/marketing/LandingScreen";
import { NotFoundScreen } from "./features/errors/NotFoundScreen";
import { SearchScreen } from "./features/search/SearchScreen";
import { TasksScreen } from "./features/tasks/TasksScreen";
import { UsersScreen } from "./features/users/UsersScreen";
import { SubmitForReviewScreen } from "./features/workflow/SubmitForReviewScreen";
import { WorkflowScreen } from "./features/workflow/WorkflowScreen";
import { ApiError } from "./lib/api";
import { AuthProvider, useAuth } from "./lib/auth";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Never retry any client error. The request was wrong, not unlucky, so
        // the answer will not change -- and 401 retries burn the refresh token.
        //
        // This used to exempt 401 and 403 only, which meant opening a document
        // that had been deleted spun through two backoffs before the 404 was
        // shown. The screen sat on "Loading..." for several seconds and then
        // reported an error it had known about immediately.
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
          return false;
        }
        return failureCount < 2;
      },
    },
  },
});

/** Requires a signed-in user, and optionally a capability. */
function Protected({
  children,
  allow,
}: {
  children: React.ReactNode;
  allow?: (user: NonNullable<ReturnType<typeof useAuth>["user"]>) => boolean;
}) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Spinner label="Signing in…" />;
  // Somebody arriving at the front door gets the front door. Somebody who
  // followed a link to a specific document wanted that document, and a
  // marketing page is not a helpful answer -- they go straight to sign-in.
  if (!user) {
    return (
      <Navigate to={location.pathname === "/" ? "/welcome" : "/login"} replace />
    );
  }
  if (allow && !allow(user)) {
    // The message names what this role lacks rather than describing it as
    // read-only: an NCM reaching the Users screen has extensive rights, just
    // not user administration, and telling them otherwise is simply wrong.
    const readOnly = !user.canUpload && !user.canReview;
    return (
      <div className="card mx-auto max-w-xl p-10 text-center">
        <h2 className="m-0 font-display text-h2serif font-normal text-ink">
          {readOnly ? "Read-only access" : "Not available to your role"}
        </h2>
        <p className="mx-auto mt-3 max-w-md text-base+ leading-[1.65] text-muted">
          {readOnly
            ? `${user.roleLabel}s can search and read published documents. Ask a Network Control Manager for the rights you need to use this area.`
            : `This area is not open to a ${user.roleLabel}. Everything else you have access to is unaffected.`}
        </p>
      </div>
    );
  }
  return <>{children}</>;
}

function Router() {
  const { user, loading } = useAuth();

  return (
    <Routes>
      <Route
        path="/welcome"
        element={
          loading ? <Spinner /> : user ? <Navigate to="/" replace /> : <LandingScreen />
        }
      />

      <Route
        path="/login"
        element={
          loading ? (
            <Spinner />
          ) : user ? (
            <Navigate to="/" replace />
          ) : (
            <LoginScreen />
          )
        }
      />

      <Route
        element={
          <Protected>
            <Shell />
          </Protected>
        }
      >
        <Route index element={<HomeScreen />} />
        <Route path="assistant" element={<AssistantScreen />} />
        <Route path="search" element={<SearchScreen />} />
        <Route path="documents" element={<DocumentListScreen />} />
        <Route path="documents/:documentId" element={<DocumentDetailScreen />} />
        <Route path="documents/:documentId/edit" element={<EditorScreen />} />
        <Route
          path="documents/:documentId/submit"
          element={<SubmitForReviewScreen />}
        />
        <Route
          path="tasks"
          element={
            <Protected allow={(u) => u.canReview || u.canUpload}>
              <TasksScreen />
            </Protected>
          }
        />
        <Route
          path="ingestion"
          element={
            <Protected allow={(u) => u.canUpload}>
              <IngestionScreen />
            </Protected>
          }
        />
        <Route
          path="workflows"
          element={
            <Protected allow={(u) => u.canUpload}>
              <WorkflowScreen />
            </Protected>
          }
        />
        <Route
          path="users"
          element={
            <Protected allow={(u) => u.canManage}>
              <UsersScreen />
            </Protected>
          }
        />
        <Route
          path="audit"
          element={
            <Protected allow={(u) => u.canViewAudit}>
              <AuditScreen />
            </Protected>
          }
        />
        <Route
          path="admin"
          element={
            <Protected allow={(u) => u.canManage || u.canPublish}>
              <AdminScreen />
            </Protected>
          }
        />

        {/* Inside the shell, so a wrong address still shows the navigation the
            reader needs to get somewhere useful. Redirecting to Home instead --
            which is what this did -- hides the fact that the link was broken,
            and reads as "the document is gone" rather than "this address is
            not a thing". */}
        <Route path="*" element={<NotFoundScreen />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <Router />
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
